import '@/css/app.css'
import { gsap } from 'gsap'
import { CustomEase } from 'gsap/CustomEase'
import barba from '@barba/core'
import Lenis from 'lenis'
import { runCookieScript } from './cookie-consent-mode.js'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(CustomEase, ScrollTrigger)

import { initWhenReady } from './base/heroAnimation'
import { initHighlightText } from './base/highlight-text'
import { initTabSystem } from './base/tabs'
import { initStackedCardsSlider } from './base/stacked-cards'
import { initGlobalParallax } from './base/parallax'
import { initScrollContainer } from './base/scrollContainer'
import { initHorizontalScrolling } from './base/hor-slider'
import { initMomentumBasedHover } from './base/momentum'
import { initAnimatedCircle } from './base/animated-circle'
import { initContentRevealScroll } from './base/revealOnScroll'
import { initRequestUpload } from './base/requestUpload'
import { initRequestUploadDiagnostics } from './base/requestUploadDiagnostics'
import { initFormieAfterBarba } from './base/formieBarba'
import { initFormieLoading } from './base/formieLoading'
import { initRequestCustomFields } from './base/requestCustomFields'
import { initFalling2DMatterJS } from './base/falling-2d-matter'
import { initFormStepTracking, trackFormThanksPage } from './base/formTracking'

// -----------------------------------------
// OSMO PAGE TRANSITION BOILERPLATE
// -----------------------------------------

history.scrollRestoration = 'manual'

let lenis = null
let nextPage = document
let onceFunctionsInitialized = false
let hasBarbaNavigated = false

const hasLenis = true
const hasScrollTrigger = typeof window.ScrollTrigger !== 'undefined'
const initialLoaderDelay = 0.5

const rmMQ = window.matchMedia('(prefers-reduced-motion: reduce)')
let reducedMotion = rmMQ.matches
rmMQ.addEventListener?.('change', (e) => (reducedMotion = e.matches))
rmMQ.addListener?.((e) => (reducedMotion = e.matches))

const has = (s) => !!nextPage.querySelector(s)

let staggerDefault = 0.05
let durationDefault = 0.6

CustomEase.create('osmo', '0.625, 0.05, 0, 1')
gsap.defaults({ ease: 'osmo', duration: durationDefault })

// -----------------------------------------
// FUNCTION REGISTRY
// -----------------------------------------

function initOnceFunctions() {
    initLenis()
    runCookieScript()
    initFormStepTracking()
    if (onceFunctionsInitialized) return
    onceFunctionsInitialized = true

    // Runs once on first load
    // if (has('[data-something]')) initSomething();
}

function initBeforeEnterFunctions(next) {
    nextPage = next || document

    // Runs before the enter animation
    // if (has('[data-something]')) initSomething();
}

async function initAfterEnterFunctions(next, html) {
    nextPage = next || document

    if (nextPage instanceof Element) {
        if (nextPage.dataset.pageFunctionsInitialized === 'true') {
            return
        }

        nextPage.dataset.pageFunctionsInitialized = 'true'
    }

    // Runs after enter animation completes
    if (has('[data-something]')) initSomething()
    initWhenReady(nextPage)
    initHighlightText(nextPage)
    initTabSystem(nextPage)
    initStackedCardsSlider(nextPage)
    initGlobalParallax(nextPage)
    initScrollContainer(nextPage)
    initHorizontalScrolling(nextPage)
    initMomentumBasedHover(nextPage)
    initContentRevealScroll(nextPage)

    if (has('[data-animated-circle]')) initAnimatedCircle(nextPage)
    if (
        hasBarbaNavigated &&
        has('form[data-formie-form], div[data-formie-form]')
    ) {
        await initFormieAfterBarba(nextPage, html)
    }
    if (has('form[data-formie-form]')) initFormieLoading(nextPage)
    if (
        has(
            '[data-custom-cat-field], [data-custom-artist-field], [data-custom-state-field], [data-custom-origin-field], [data-custom-sell-field]'
        )
    ) {
        initRequestCustomFields(nextPage)
    }
    if (has('[data-formie-upload-manager-root]')) {
        initRequestUpload(nextPage)
        initRequestUploadDiagnostics(nextPage)
    }
    if (has('#canvas-target')) initFalling2DMatterJS(nextPage)

    trackFormThanksPage(nextPage)

    if (hasLenis) {
        lenis.resize()
    }

    if (hasScrollTrigger) {
        ScrollTrigger.refresh()
    }
}

// -----------------------------------------
// PAGE TRANSITIONS
// -----------------------------------------

function runPageOnceAnimation(next) {
    const wrap = document.querySelector('[data-transition-wrap]')
    const panel = wrap?.querySelector('[data-transition-panel]')
    const logo = wrap?.querySelector('[data-transition-logo]')
    const shouldSkipInitialLoader = next?.dataset?.sectionType === 'pageThanks'

    if (shouldSkipInitialLoader || reducedMotion || !panel) {
        const tl = gsap.timeline()

        tl.call(
            () => {
                resetPage(next)
            },
            null,
            0
        )

        return tl
    }

    gsap.set(next, {
        autoAlpha: 0
    })

    gsap.set(panel, {
        scaleY: 1,
        transformOrigin: 'bottom',
        backgroundColor: '#352414'
    })

    const animation = gsap.timeline()
    animation.add(createPageEnterTimeline(next), initialLoaderDelay)

    animation.set(
        panel,
        {
            clearProps: 'backgroundColor'
        },
        initialLoaderDelay + 1.8
    )

    if (logo) {
        gsap.set(logo, {
            autoAlpha: 1
        })

        animation.to(
            logo,
            {
                autoAlpha: 0,
                duration: 0.3
            },
            initialLoaderDelay + 1.2
        )
        animation.set(
            logo,
            {
                autoAlpha: 0
            },
            initialLoaderDelay + 1.8
        )
    }

    return animation
}

function runPageLeaveAnimation(current, next) {
    const wrap = document.querySelector('[data-transition-wrap]')
    const panel = wrap.querySelector('[data-transition-panel]')

    const tl = gsap.timeline({
        onComplete: () => {
            current.remove()
        }
    })

    if (reducedMotion) {
        // Immediate swap behavior if user prefers reduced motion
        return tl.set(current, { autoAlpha: 0 })
    }
    tl.set(panel, {
        transformOrigin: 'top'
    })

    tl.to(panel, {
        scaleY: 1
    })

    return tl
}

function createPageEnterTimeline(next) {
    const wrap = document.querySelector('[data-transition-wrap]')
    const panel = wrap.querySelector('[data-transition-panel]')

    const tl = gsap.timeline()

    if (reducedMotion) {
        // Immediate swap behavior if user prefers reduced motion
        tl.set(next, { autoAlpha: 1 })
        tl.add('pageReady')
        tl.call(resetPage, [next], 'pageReady')
        return tl
    }

    const euros = createTransitionEuros(wrap)
    const panelHideDuration = 0.6

    tl.add('euroFall', 0)
    tl.add('startEnter', 1.2)
    tl.add('panelGone', `startEnter+=${panelHideDuration}`)
    tl.to(
        euros,
        {
            y: () => window.innerHeight + gsap.utils.random(80, 180),
            rotation: () => gsap.utils.random(-35, 35),
            duration: 1,
            ease: 'power2.in',
            stagger: {
                each: 0.03,
                from: 'random'
            }
        },
        'euroFall'
    )
    tl.set(
        next,
        {
            autoAlpha: 1
        },
        'startEnter'
    )
    tl.set(
        panel,
        {
            transformOrigin: 'bottom'
        },
        'startEnter'
    )
    tl.to(
        panel,
        {
            scaleY: 0,
            duration: panelHideDuration
        },
        'startEnter'
    )

    // tl.fromTo(
    //     next,
    //     {
    //         autoAlpha: 0
    //     },
    //     {
    //         autoAlpha: 1
    //     },
    //     'startEnter'
    // )

    tl.add('pageReady')
    tl.call(
        () => {
            euros.forEach((euro) => euro.remove())
        },
        null,
        'panelGone'
    )
    tl.call(resetPage, [next], 'pageReady')

    return tl
}

function runPageEnterAnimation(next) {
    const tl = createPageEnterTimeline(next)

    return new Promise((resolve) => {
        tl.call(resolve, null, 'pageReady')
    })
}

function createTransitionEuros(wrap) {
    const template = wrap?.querySelector('[data-transition-euro]')
    if (!template) return []

    const amount = 18
    const viewportWidth = window.innerWidth
    const euros = []

    gsap.set(template, {
        position: 'absolute',
        top: 0,
        left: 0,
        autoAlpha: 0,
        yPercent: -100,
        pointerEvents: 'none'
    })

    for (let i = 0; i < amount; i++) {
        const euro = template.cloneNode(true)
        const media = euro.querySelector('img, svg')
        euro.removeAttribute('data-transition-euro')
        euro.classList.remove(
            'pointer-events-none',
            'absolute',
            'top-0',
            'left-0',
            '-translate-y-full',
            'opacity-0'
        )
        euro.dataset.transitionEuroClone = ''
        wrap.appendChild(euro)

        const size = gsap.utils.random(150, 200)

        gsap.set(euro, {
            autoAlpha: 1,
            display: 'block',
            visibility: 'visible',
            opacity: 1,
            position: 'absolute',
            top: 0,
            left: gsap.utils.random(0, viewportWidth),
            zIndex: 20,
            width: size,
            height: size,
            y: gsap.utils.random(-320, -200),
            xPercent: -50,
            scale: gsap.utils.random(0.75, 1.35),
            rotation: gsap.utils.random(-20, 20),
            transformOrigin: '50% 50%',
            pointerEvents: 'none'
        })

        if (media) {
            gsap.set(media, {
                display: 'block',
                width: '100%',
                height: '100%'
            })
        }

        euros.push(euro)
    }

    return euros
}

// -----------------------------------------
// BARBA HOOKS + INIT
// -----------------------------------------

barba.hooks.beforeEnter((data) => {
    // Position new container on top
    gsap.set(data.next.container, {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        autoAlpha: 0
    })

    if (lenis && typeof lenis.stop === 'function') {
        lenis.stop()
    }

    initBeforeEnterFunctions(data.next.container)
    applyThemeFrom(data.next.container)
})

barba.hooks.afterLeave(() => {
    if (hasScrollTrigger) {
        ScrollTrigger.getAll().forEach((trigger) => trigger.kill())
    }
})

barba.hooks.enter((data) => {
    initBarbaNavUpdate(data)
})

barba.hooks.afterEnter(async (data) => {
    // Run page functions
    await initAfterEnterFunctions(data.next.container, data.next.html)

    // Settle
    if (hasLenis) {
        lenis.resize()
        lenis.start()
    }

    if (hasScrollTrigger) {
        ScrollTrigger.refresh()
    }
})

barba.init({
    debug: true, // Set to 'false' in production
    timeout: 7000,
    preventRunning: true,
    transitions: [
        {
            name: 'default',
            sync: true,

            // First load
            async once(data) {
                initOnceFunctions()

                const animation = runPageOnceAnimation(data.next.container)

                await initAfterEnterFunctions(
                    data.next.container,
                    data.next.html
                )

                return animation
            },

            // Current page leaves
            async leave(data) {
                hasBarbaNavigated = true

                return runPageLeaveAnimation(
                    data.current.container,
                    data.next.container
                )
            },

            // New page enters
            async enter(data) {
                return runPageEnterAnimation(data.next.container)
            }
        }
    ]
})

// -----------------------------------------
// GENERIC + HELPERS
// -----------------------------------------

const themeConfig = {
    light: {
        nav: 'dark',
        transition: 'light'
    },
    dark: {
        nav: 'light',
        transition: 'dark'
    }
}

function applyThemeFrom(container) {
    const pageTheme = container?.dataset?.pageTheme || 'light'
    const config = themeConfig[pageTheme] || themeConfig.light

    document.body.dataset.pageTheme = pageTheme
    const transitionEl = document.querySelector('[data-theme-transition]')
    if (transitionEl) {
        transitionEl.dataset.themeTransition = config.transition
    }

    const nav = document.querySelector('[data-theme-nav]')
    if (nav) {
        nav.dataset.themeNav = config.nav
    }
}

function initLenis() {
    console.log(lenis)
    if (lenis) return // already created

    if (!hasLenis) return
    lenis = new Lenis({
        lerp: 0.165,
        wheelMultiplier: 1.25,
        prevent: (node) => {
            const classes = ['pm-wrapper']
            return (
                node.hasAttribute('data-lenis-prevent') ||
                node.closest('[data-lenis-prevent]') ||
                classes.some((cls) => node.classList.contains(cls))
            )
        }
    })

    if (hasScrollTrigger) {
        lenis.on('scroll', ScrollTrigger.update)
    }

    gsap.ticker.add((time) => {
        lenis.raf(time * 1000)
    })

    gsap.ticker.lagSmoothing(0)
}

function resetPage(container) {
    window.scrollTo(0, 0)
    gsap.set(container, {
        clearProps: 'position,top,left,right,opacity,visibility'
    })

    if (hasLenis) {
        lenis.resize()
        lenis.start()
    }
}

function debounceOnWidthChange(fn, ms) {
    let last = innerWidth,
        timer
    return function (...args) {
        clearTimeout(timer)
        timer = setTimeout(() => {
            if (innerWidth !== last) {
                last = innerWidth
                fn.apply(this, args)
            }
        }, ms)
    }
}

function initBarbaNavUpdate(data) {
    var tpl = document.createElement('template')
    tpl.innerHTML = data.next.html.trim()
    var nextNodes = tpl.content.querySelectorAll('[data-barba-update]')
    var currentNodes = document.querySelectorAll('nav [data-barba-update]')

    currentNodes.forEach(function (curr, index) {
        var next = nextNodes[index]
        if (!next) return

        // Aria-current sync
        var newStatus = next.getAttribute('aria-current')
        if (newStatus !== null) {
            curr.setAttribute('aria-current', newStatus)
        } else {
            curr.removeAttribute('aria-current')
        }

        // Class list sync
        var newClassList = next.getAttribute('class') || ''
        curr.setAttribute('class', newClassList)
    })
}

// -----------------------------------------
// YOUR FUNCTIONS GO BELOW HERE
// -----------------------------------------
